
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";

export interface HistoricalNodeData {
  rawDate: string;
  date: Date;
  displayDate: string;
  value: number;
}

export interface HistoricalNodesResponse {
  historicalData: HistoricalNodeData[];
  lastTwoValues: { total_addresses: number }[];
}

export function useHistoricalNodesData() {
  return useQuery({
    queryKey: ['historicalNodes'],
    queryFn: async (): Promise<HistoricalNodesResponse> => {
      console.log("Fetching historical active nodes data");
      
      // Get the past 14 days of daily data
      const fourteenDaysAgo = new Date();
      fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14);
      const fourteenDaysAgoString = fourteenDaysAgo.toISOString().split('T')[0];
      
      const { data: dailyData, error: dailyError } = await supabase
        .from('daily_aggregated_stats')
        .select('date, total_addresses')
        .gte('date', fourteenDaysAgoString)
        .order('date', { ascending: true });

      if (dailyError) {
        console.error("Error fetching daily nodes data:", dailyError);
        throw dailyError;
      }
      
      console.log("Daily nodes data fetched:", dailyData?.length || 0, "records");
      console.log("First nodes record:", dailyData?.[0]);
      console.log("Last nodes record:", dailyData?.[dailyData?.length - 1]);

      // Get the last two daily values for difference calculation
      const { data: lastTwo } = await supabase
        .from('daily_aggregated_stats')
        .select('total_addresses')
        .order('date', { ascending: false })
        .limit(2);

      if (!dailyData || dailyData.length === 0) {
        console.log("No daily nodes data available");
        return {
          historicalData: [],
          lastTwoValues: []
        };
      }

      // Process data for chart
      const chartData = dailyData.map(item => {
        const date = new Date(item.date);
        return {
          rawDate: item.date,
          date: date,
          displayDate: format(date, 'MMM dd'),
          value: Number(item.total_addresses || 0)
        };
      });
      
      console.log("Processed nodes chart data:", chartData.length, "records");
      
      return {
        historicalData: chartData,
        lastTwoValues: lastTwo || []
      };
    }
  });
}
